import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Box,
  CircularProgress,
  Grid,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { BORDER_RADIUS, COLORS } from "app/theme";
import { useUIStore } from "stores";
import MilkQualityAlertCard from "./components/MilkQualityAlertCard";
import MilkQualityDistributionChart from "./components/MilkQualityDistributionChart";
import MilkQualityTrendChart from "./components/MilkQualityTrendChart";
import {
  useBirdPieData,
  useCarcaseAverages,
  useHabitatByName,
  useHighLevelMetrics,
  useMilkQualityAverages,
  useMilkQualityPerFarmerPeriod,
  useMilkQualityTrend,
  useProgrammeInfo,
} from "./hooks/useInsightsDashboardQueries";
import InsightsSection from "./InsightsSection";
import {
  BirdObservationsSection,
  getBirdContext,
  ProgrammeHabitatChart,
} from "./tabs/BiodiversityTab";

// ─── Banner config (matching ProgrammeBanner.tsx) ───────────────

const PROGRAMME_BANNERS: Record<string, { image: string; bgColor: string }> = {
  "6vczLKixKSwPkIFP3JkkGD": {
    image:
      "https://ruumi-dashboard-assets.ruumi.io/lidl-sustainable-beef-banner.png",
    bgColor: "#395499",
  },
  "3UqQUHVbKY5CMBthVL5Ltq": {
    image: "https://ruumi-dashboard-assets.ruumi.io/Pattemores.png",
    bgColor: "#ffffff",
  },
  "5OPgsM2LDKgFHqhLO4Kzpv": {
    image: "https://ruumi-dashboard-assets.ruumi.io/County-Milk-2.png",
    bgColor: "#ffffff",
  },
  "4frNWjGfIiNgKS5gzM3JIQ": {
    //staging
    image: "https://ruumi-dashboard-assets.ruumi.io/Dovecote-Park.png",
    bgColor: "#ffffff",
  },
  "3Z1DiaL6bsi2XXfkUMcrQR": {
    //prod
    image: "https://ruumi-dashboard-assets.ruumi.io/Dovecote-Park.png",
    bgColor: "#ffffff",
  },
};

const BELLFLOWER_BORDER = "#D3C9E7";

// ─── Styled components (matching farmer card system) ─────────────

const Card = styled(Box)({
  backgroundColor: "white",
  borderRadius: BORDER_RADIUS.medium,
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
  padding: 20,
});

// ─── Metric components (matching FarmMetricsCards.tsx) ────────────

function MetricItem({
  label,
  value,
  unit,
  tooltip,
}: {
  label: string;
  value: string;
  unit?: string;
  tooltip?: string;
}) {
  return (
    <Tooltip title={value} enterDelay={500} enterNextDelay={500}>
      <Box
        sx={{
          py: 0.75,
          borderBottom: "1px solid #F0F0F0",
          "&:last-child": { borderBottom: "none" },
          minWidth: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              display: "block",
              fontSize: "0.7rem",
            }}
          >
            {label}
          </Typography>
          {tooltip && (
            <Tooltip title={tooltip} arrow placement="top">
              <InfoOutlinedIcon
                sx={{ fontSize: 13, color: "#a1a1aa", cursor: "help" }}
              />
            </Tooltip>
          )}
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            gap: 0.5,
            minWidth: 0,
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              color: "#1a1a2e",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {value}
          </Typography>
          {unit && (
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontSize: "0.7rem",
                flexShrink: 0,
              }}
            >
              {unit}
            </Typography>
          )}
        </Box>
      </Box>
    </Tooltip>
  );
}

function MetricsCard({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: 2,
        px: 1.5,
        py: 0.5,
        border: `1px solid ${BELLFLOWER_BORDER}`,
      }}
    >
      {children}
    </Box>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────

function fmt(
  value: number | string | undefined | null,
  maxDecimals = 1
): string {
  if (value == null) return "-";
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return "-";
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDecimals,
  });
}

function LoadingBox() {
  return (
    <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
      <CircularProgress size={32} />
    </Box>
  );
}

// ─── Metric row (reused for animal perf) ─────────────────────────

function MetricRow({
  label,
  value,
  unit,
  color,
  tooltip,
}: {
  label: string;
  value: string;
  unit: string;
  color: string;
  tooltip?: string;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        py: 0.5,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            backgroundColor: color,
            flexShrink: 0,
          }}
        />
        <Typography variant="body2" sx={{ color: "#71717a" }}>
          {label}
        </Typography>
        {tooltip && (
          <Tooltip title={tooltip} arrow placement="top">
            <InfoOutlinedIcon
              sx={{ fontSize: 13, color: "#a1a1aa", cursor: "help" }}
            />
          </Tooltip>
        )}
      </Box>
      <Typography variant="body2" sx={{ fontWeight: 600, color: "#18181b" }}>
        {value}
        <Typography
          component="span"
          variant="caption"
          sx={{ color: "#a1a1aa", ml: 0.5 }}
        >
          {unit}
        </Typography>
      </Typography>
    </Box>
  );
}

// ─── Programme Banner ───────────────────────────────────────────

function ProgrammeBannerHeader({
  guid,
  title,
}: {
  guid: string | null;
  title: string | null;
}) {
  const banner = guid ? PROGRAMME_BANNERS[guid] : null;
  return (
    <Box
      sx={{
        position: "relative",
        height: 140,
        borderRadius: `${BORDER_RADIUS.medium}px`,
        overflow: "hidden",
        mx: 3,
        mt: 1,
        mb: 2,
        ...(banner
          ? {
              backgroundImage: `url("${banner.image}")`,
              backgroundSize: "contain",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              backgroundColor: banner.bgColor,
            }
          : {
              background: `linear-gradient(135deg, ${COLORS.dahlia[900]} 0%, ${COLORS.dahlia[700]} 50%, ${COLORS.dahlia[500]} 100%)`,
            }),
      }}
    >
      {!banner && title && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 3,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: "white",
              fontWeight: 700,
              textAlign: "center",
              textShadow: "0 1px 4px rgba(0,0,0,0.2)",
            }}
          >
            {title}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

// ─── Main component ─────────────────────────────────────────────

export function ProjectInsightsMetrics() {
  const { data: programmeInfo } = useProgrammeInfo();
  const programmeGuid =
    programmeInfo?.data?.[0]?.["HighLevelMetrics.programmeGuid"] ?? null;
  const programmeType =
    programmeInfo?.data?.[0]?.["HighLevelMetrics.programmeType"] ?? "BEEF";
  const isDairy = programmeType === "DAIRY";
  const dashboardTitle = useUIStore(
    (state) => state.selectedProjectDashboard?.title
  );
  const { data: hlm, isLoading: hlmLoading } = useHighLevelMetrics();
  const { data: carcase, isLoading: carcaseLoading } = useCarcaseAverages();
  const { data: milkQuality, isLoading: milkLoading } =
    useMilkQualityAverages();
  const { data: milkPerFarmer, isLoading: milkPerFarmerLoading } =
    useMilkQualityPerFarmerPeriod();
  const { data: milkTrend, isLoading: milkTrendLoading } =
    useMilkQualityTrend();
  const { data: birdPieData, isLoading: birdPieLoading } = useBirdPieData();
  const { data: habitatByName, isLoading: habitatLoading } = useHabitatByName();

  const hlmRow = hlm?.data?.[0];
  const carcaseRow = carcase?.data?.[0];
  const milkRow = milkQuality?.data?.[0];
  const birdSpecies: string[] = (birdPieData?.data ?? [])
    .map((r) => r["BirdObservations.bird"])
    .filter(Boolean);

  const habitats = (habitatByName?.data ?? [])
    .filter((r) => r["Biodiversity.habitatName"])
    .map((r) => ({
      name: r["Biodiversity.habitatName"],
      group: r["Biodiversity.habitatGroup"],
      areaHa: Number(r["Biodiversity.totalAreaHa"]) || 0,
      lengthM: Number(r["Biodiversity.totalLengthM"]) || 0,
    }));

  const redListCount = birdSpecies.filter(
    (name) => getBirdContext(name).status === "Red list"
  ).length;
  const amberListCount = birdSpecies.filter(
    (name) => getBirdContext(name).status === "Amber list"
  ).length;
  const habitatGroupCount = new Set(
    habitats.map((h) => h.group).filter(Boolean)
  ).size;
  const totalAreaHa = habitats.reduce((sum, h) => sum + h.areaHa, 0);

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        minHeight: "100%",
        paddingTop: "12px",
        paddingBottom: "20px",
        overflowX: "hidden",
      }}
    >
      {/* Programme Banner */}
      <ProgrammeBannerHeader
        guid={programmeGuid}
        title={dashboardTitle ?? null}
      />

      {/* Programme KPIs */}
      <InsightsSection title="Programme Overview">
        {hlmLoading ? (
          <LoadingBox />
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr 1fr" },
              gap: 1,
            }}
          >
            <MetricsCard>
              <MetricItem
                label="Farmers"
                value={fmt(hlmRow?.["HighLevelMetrics.sumFarmers"], 0)}
                tooltip="The number of farmers enrolled on the programme."
              />
              <MetricItem
                label="Acres"
                value={fmt(hlmRow?.["HighLevelMetrics.sumAcres"], 0)}
                tooltip="The total number of acres within the programme."
              />
            </MetricsCard>
            <MetricsCard>
              <MetricItem
                label="Actions"
                value={fmt(hlmRow?.["HighLevelMetrics.sumActions"], 0)}
                tooltip="The total number of individual farmer actions being undertaken on the programme."
              />
              <MetricItem
                label="Farm Action Plans"
                value={fmt(hlmRow?.["HighLevelMetrics.sumFarmActionPlans"], 0)}
                tooltip="The total number of programme Farm Action Plans agreed."
              />
            </MetricsCard>
            <MetricsCard>
              <MetricItem
                label="Biodiversity Assessments"
                value={fmt(
                  hlmRow?.["HighLevelMetrics.sumBiodiversityAssessments"],
                  0
                )}
                tooltip="A report which looks at the farmer's current biodiversity awareness and provides advice on how to improve biodiversity."
              />
              <MetricItem
                label="Soil Assessments"
                value={fmt(hlmRow?.["HighLevelMetrics.sumSoilAssessments"], 0)}
                tooltip="A report which assesses a farmer's land management practices and provides advice on how to improve soil health and water quality."
              />
            </MetricsCard>
          </Box>
        )}
      </InsightsSection>

      {/* Performance metrics — conditional on programme type */}
      {isDairy ? (
        <InsightsSection title="Milk Quality">
          <MilkQualityAlertCard
            data={milkPerFarmer?.data}
            isLoading={milkPerFarmerLoading}
          />
          {milkLoading ? (
            <LoadingBox />
          ) : (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Card sx={{ height: "100%" }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: "#18181b", mb: 1.5 }}
                  >
                    Quality Metrics
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    <MetricRow
                      label="Butterfat"
                      value={fmt(milkRow?.["MilkQuality.avgFatPercent"], 2)}
                      unit="%"
                      color={COLORS.dahlia[500]}
                    />
                    <MetricRow
                      label="Protein"
                      value={fmt(milkRow?.["MilkQuality.avgProteinPercent"], 2)}
                      unit="%"
                      color={COLORS.bellflower[500]}
                    />
                    <MetricRow
                      label="Somatic Cell Count"
                      value={fmt(
                        milkRow?.["MilkQuality.avgSomaticCellCount"],
                        0
                      )}
                      unit="cells/ml"
                      color={COLORS.daisy[500]}
                    />
                    <MetricRow
                      label="Bacterial Count"
                      value={fmt(
                        milkRow?.["MilkQuality.avgTotalBacterialCount"],
                        0
                      )}
                      unit="cfu/ml"
                      color={COLORS.dahlia[300]}
                    />
                  </Box>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Card sx={{ height: "100%" }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: "#18181b", mb: 1.5 }}
                  >
                    Programme Metrics
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    <MetricRow
                      label="Avg. Volume"
                      value={fmt(milkRow?.["MilkQuality.avgVolumeLiters"], 0)}
                      unit="litres"
                      color={COLORS.dahlia[500]}
                    />
                    <MetricRow
                      label="Grazing Days"
                      value={fmt(
                        hlmRow?.["HighLevelMetrics.sumGrazingDays"],
                        0
                      )}
                      unit="days"
                      color={COLORS.bellflower[500]}
                      tooltip="The average number of days per year that animals, within the programme, spent outdoors grazing."
                    />
                    <MetricRow
                      label="Avg Recovery Time"
                      value={fmt(hlmRow?.["HighLevelMetrics.sumRecoveryTime"])}
                      unit="days"
                      color={COLORS.daisy[500]}
                    />
                  </Box>
                </Card>
              </Grid>
            </Grid>
          )}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            <MilkQualityDistributionChart
              title="Somatic Cell Count — Farmer Distribution"
              data={milkPerFarmer?.data}
              metric="scc"
              isLoading={milkPerFarmerLoading}
            />
            <MilkQualityDistributionChart
              title="Total Bacterial Count — Farmer Distribution"
              data={milkPerFarmer?.data}
              metric="tbc"
              isLoading={milkPerFarmerLoading}
            />
            <MilkQualityTrendChart
              data={milkTrend?.data}
              isLoading={milkTrendLoading}
            />
          </Box>
        </InsightsSection>
      ) : (
        <InsightsSection title="Animal Performance">
          {hlmLoading || carcaseLoading ? (
            <LoadingBox />
          ) : (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Card sx={{ height: "100%" }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: "#18181b", mb: 1.5 }}
                  >
                    Carcase Metrics
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    <MetricRow
                      label="Daily Liveweight Gain"
                      value={fmt(
                        carcaseRow?.["CarcaseSummary.avgDailyLifeweightGain"],
                        2
                      )}
                      unit="kg/day"
                      color={COLORS.dahlia[500]}
                      tooltip="Average Daily Liveweight Gain (kgs) across all programme cattle. The higher an animal's daily lifetime growth, the more efficient it is, and the lower its carbon footprint per kg of beef will be."
                    />
                    <MetricRow
                      label="Avg. Slaughter Age"
                      value={fmt(
                        carcaseRow?.["CarcaseSummary.avgAgeAtSlaughter"],
                        0
                      )}
                      unit="months"
                      color={COLORS.bellflower[500]}
                      tooltip="Average Age at Slaughter (months) across all programme cattle. While reducing age at slaughter can have a positive impact on carbon emissions, this metric is closely linked to carcase weight and retail specification and so it is important that this metric is not taken in isolation. Genetic and environmental factors such as breed and nutrition will influence on age at slaughter."
                    />
                    <MetricRow
                      label="Avg. Carcase Weight"
                      value={fmt(
                        carcaseRow?.["CarcaseSummary.avgCarcaseColdWeight"],
                        0
                      )}
                      unit="kg"
                      color={COLORS.daisy[500]}
                      tooltip="Average Carcase Cold Weight (kgs) across all programme cattle. The desired carcase cold weight to meet retail specification is between 270 - 420kgs. Genetic and environmental factors such as breed or nutrition will influence carcase cold weight. This metric is also linked to age at slaughter."
                    />
                    <MetricRow
                      label="Grade Specification"
                      value={
                        carcaseRow?.[
                          "CarcaseSummary.avgCarcaseWithinDesiredGrade"
                        ] != null
                          ? `${(
                              carcaseRow[
                                "CarcaseSummary.avgCarcaseWithinDesiredGrade"
                              ] * 100
                            ).toFixed(0)}`
                          : "-"
                      }
                      unit="%"
                      color={COLORS.dahlia[300]}
                      tooltip="The average % of carcases within the desired grade specification across all programme cattle."
                    />
                  </Box>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Card sx={{ height: "100%" }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: "#18181b", mb: 1.5 }}
                  >
                    Programme Metrics
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    <MetricRow
                      label="PCF"
                      value={fmt(hlmRow?.["HighLevelMetrics.sumPcf"])}
                      unit="kg CO₂e/kg"
                      color={COLORS.dahlia[500]}
                      tooltip="The average carbon footprint of animals within the programme. A wide range of production systems and environmental conditions directly influence carbon footprint emissions, which can vary season to season."
                    />
                    <MetricRow
                      label="Grazing Days"
                      value={fmt(
                        hlmRow?.["HighLevelMetrics.sumGrazingDays"],
                        0
                      )}
                      unit="days"
                      color={COLORS.bellflower[500]}
                      tooltip="The average number of days per year that animals, within the programme, spent outdoors grazing."
                    />
                    <MetricRow
                      label="Avg Recovery Time"
                      value={fmt(hlmRow?.["HighLevelMetrics.sumRecoveryTime"])}
                      unit="days"
                      color={COLORS.daisy[500]}
                    />
                  </Box>
                </Card>
              </Grid>
            </Grid>
          )}
        </InsightsSection>
      )}

      {/* Biodiversity Summary */}
      <InsightsSection title="Biodiversity Summary">
        {birdPieLoading || habitatLoading ? (
          <LoadingBox />
        ) : (
          <>
            {/* Scalar metrics */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr 1fr 1fr" },
                gap: 1,
                mb: 2,
              }}
            >
              <MetricsCard>
                <MetricItem
                  label="Red List Species"
                  value={String(redListCount)}
                  unit="observed"
                  tooltip="The number of birds detected during the biodiversity assessment using visual surveys and the Merlin App across all programme farms that are on the Red conservation list."
                />
              </MetricsCard>
              <MetricsCard>
                <MetricItem
                  label="Amber List Species"
                  value={String(amberListCount)}
                  unit="observed"
                  tooltip="The number of birds detected during the biodiversity assessment using visual surveys and the Merlin App across all programme farms that are on the Amber conservation list."
                />
              </MetricsCard>
              <MetricsCard>
                <MetricItem
                  label="Habitat Groups"
                  value={String(habitatGroupCount)}
                  unit="surveyed"
                  tooltip="Each farm has been mapped and the different land uses have been categorised into the appropriate habitat types."
                />
              </MetricsCard>
              <MetricsCard>
                <MetricItem
                  label="Total Area Surveyed"
                  value={fmt(totalAreaHa)}
                  unit="ha"
                />
              </MetricsCard>
            </Box>

            {/* Interactive habitat chart */}
            {habitatLoading ? (
              <LoadingBox />
            ) : (
              habitats.length > 0 && (
                <ProgrammeHabitatChart habitats={habitats} />
              )
            )}

            {/* Interactive bird observations */}
            {birdPieLoading ? (
              <LoadingBox />
            ) : (
              birdSpecies.length > 0 && (
                <BirdObservationsSection
                  species={birdSpecies}
                  subtitle={`${birdSpecies.length} species recorded across the programme`}
                />
              )
            )}
          </>
        )}
      </InsightsSection>
    </Box>
  );
}
